import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BLOCKER_SHORT_KEYS, NOTICE_LABEL_KEYS, ORDER_STATUS, PRIORITY_LABEL_KEYS, formatActivityDate, formatReceived } from "./orderLabels";
import { getOrderLog, ordersKeys, type CarrierCount, type ChannelChange, type OrderLogEvent, type OrderPriority } from "./orders.api";
import { carriersText, formatNumber } from "./orderSummary";
import { requirementLabelKey } from "../locations/requirements";
import { formatDecimal, formatEuro } from "../../lib/money";
import { PRICE_SOURCE_KEYS } from "./ManualPriceFields";
import type { PriceSource } from "./manualPrices.api";
import { PALLET_KIND_LABEL_KEYS } from "@/features/pallets/pallets.api";
import { LEG_KIND_LABEL_KEYS, type LegKind } from "@/features/trips/trips.api";
import { driverReportHeadline, driverReportLines } from "./driverReportLines";

// The log of one order: what happened to it, who did it and what changed, field by field
// (Camiel, 17-09-2026: "alles wat men handmatig bewerkt moet gelogd worden"). The API
// writes the events; this reads them back in words.

const EVENT_LABEL_KEYS: Record<string, string> = {
  "order.created": "orders.logCreated",
  "order.updated": "orders.logUpdated",
  "order.accepted": "orders.logAccepted",
  "order.rejected": "orders.logRejected",
  "order.cancelled": "orders.logCancelled",
  // On hold and released again (docs/tms-orderintake.md §3l).
  "order.held": "orders.logHeld",
  "order.released": "orders.logReleased",
  // From before the silent intake (S44); stored events keep their name.
  "order.confirmed": "orders.logConfirmed",
  "order.created_and_confirmed": "orders.logCreatedAndConfirmed",
  // Direct or through a depot, the planner's decision (25-09-2026).
  "order.route_changed": "orders.logRouteChanged",
  "order.depot_pinned": "orders.logDepotPinned",
  // A price by hand and planning's approval (docs/tms-facturatie.md §24.8).
  "order.price_set": "orders.logPriceSet",
  "order.price_changed": "orders.logPriceChanged",
  "order.price_approved": "orders.logPriceApproved",
  "order.price_removed": "orders.logPriceRemoved",
  // Waiting hours to charge and not to be invoiced (docs/tms-facturatie.md §27).
  "order.waiting_set": "orders.logWaitingSet",
  "order.waiting_changed": "orders.logWaitingChanged",
  "order.waiting_removed": "orders.logWaitingRemoved",
  "order.no_invoice_set": "orders.logNoInvoiceSet",
  "order.no_invoice_removed": "orders.logNoInvoiceRemoved",
  // No signed CMR comes back, ticked on the tab Documenten (03-11-2026).
  "order.no_cmr_return_set": "orders.logNoCmrReturnSet",
  "order.no_cmr_return_removed": "orders.logNoCmrReturnRemoved",
  // The pallets exchanged at a loading or unloading, and their check (docs/tms-emballage.md).
  "order.pallets_changed": "orders.logPalletsChanged",
  "order.pallets_checked": "orders.logPalletsChecked",
  "order.pallets_reopened": "orders.logPalletsReopened",
  // A mail to the customer about its transport (docs/tms-klantmails.md).
  "order.customer_mail": "orders.logCustomerMail",
  // What the driver reported, its handling, and the emballage he took back (docs/tms-afwijkingen-chauffeur.md §5.3, §5.4).
  "order.driver_reported": "orders.logDriverReported",
  "order.driver_report_handled": "orders.logDriverReportHandled",
  "order.driver_report_reopened": "orders.logDriverReportReopened",
  "order.emballage_returned": "orders.logEmballageReturned",
  // Announced to the place, taken back, or the trip mailed to it (docs/tms-aanmelden.md).
  "order.pre_notified": "preNotify.logMarked",
  "order.pre_notification_cleared": "preNotify.logCleared",
  "order.trip_announced": "preNotify.logTripMailed",
  // A packing slip the customer added on the portal after sending (docs/partner-portal.md §17a).
  "order.packing_slip_added": "orders.logPackingSlipAdded",
  // Herhalen, and the customer's own order in the place of a provisional one (docs/tms-orderintake.md §3g).
  "order.repeated": "orders.logRepeated",
  "order.replaced_provisional": "orders.logReplacedProvisional",
  // Remarks on the order and its tasks (docs/tms-orderintake.md §3k, docs/tasks.md § Taken bij een order).
  "order.comment_added": "orderNotes.logCommentAdded",
  "order.comment_withdrawn": "orderNotes.logCommentWithdrawn",
  "order.task_created": "orderNotes.logTaskCreated",
  "order.task_assigned": "orderNotes.logTaskAssigned",
  "order.task_done": "orderNotes.logTaskDone",
  "order.task_cancelled": "orderNotes.logTaskCancelled",
  "order.task_reopened": "orderNotes.logTaskReopened",
  "order.task_linked": "orderNotes.logTaskLinked",
  "order.task_unlinked": "orderNotes.logTaskUnlinked",
};

const CUSTOMER_MAIL_KEYS: Record<string, string> = {
  order_confirmation: "orders.logCustomerMailConfirmation",
  delivered: "orders.logCustomerMailDelivered",
  planned: "orders.logCustomerMailPlanned",
  emballage_incomplete: "orders.logCustomerMailEmballage",
  pre_advice: "orders.logCustomerMailPreAdvice",
};

const ROUTE_LABEL_KEYS: Record<string, string> = { direct: "planning.kindDirect", depot: "planning.routeViaDepot" };

/** Where the driver reported it: loading or unloading, at the customer or at one of our depots. */
const DRIVER_HANDLING_KEYS: Record<string, string> = {
  load: "orders.activityLoad",
  unload: "orders.activityUnload",
  depot_load: "trips.depotLoad",
  depot_unload: "trips.depotUnload",
};

/** What a changed field is called on screen. */
const FIELD_LABEL_KEYS: Record<string, string> = {
  serviceType: "orders.serviceTypeLabel",
  description: "orders.descriptionLabel",
  pallets: "orders.palletsLabel",
  carriers: "orders.carriersLabel",
  weightKg: "orders.weightLabel",
  loadingMeters: "orders.loadingMetersLabel",
  volumeM3: "orders.volumeLabel",
  temperatureMinC: "orders.temperatureMinLabel",
  temperatureMaxC: "orders.temperatureMaxLabel",
  adr: "orders.adrLabel",
  adrCode: "orders.adrCodeLabel",
  truckMountedForklift: "orders.forkliftLabel",
  requirements: "orders.requirementsLabel",
  // Events from before the rename (S50) still say "features".
  features: "orders.requirementsLabel",
  customerReference: "orders.consignmentReferenceLabel",
  remark: "orders.remarkLabel",
  window: "orders.colWindow",
  reference: "orders.colActivityReference",
  instruction: "orders.colInstruction",
  contactName: "orders.contactNameLabel",
  contactPhone: "orders.contactPhoneLabel",
  arrivalPopup: "orders.arrivalPopupLabel",
  // What a channel changes (23-09-2026).
  place: "orders.activityPlaceLabel",
  placeName: "orders.activityPlaceNameLabel",
  consignments: "orders.logConsignmentCount",
  status: "orders.colStatus",
  priority: "orders.priorityLabel",
  pendel: "orders.pendelLabel",
  tripMark: "orders.tripMarkLabel",
  stopRequirements: "orders.stopRequirements",
  announced: "orders.announcedLabel",
};

interface Change {
  from: unknown;
  to: unknown;
}

function isChange(value: unknown): value is Change {
  return typeof value === "object" && value !== null && "from" in value && "to" in value;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function asArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter((entry): entry is Record<string, unknown> => asRecord(entry) !== null) : [];
}

/** What a channel changes, one line each: where, what, from and to (channelUpdates.ts). */
export function useChangeLines(): (changes: readonly ChannelChange[]) => string[] {
  const { t } = useTranslation();
  return (changes) =>
    changes.map((change) => {
      const where =
        change.activity !== undefined
          ? `${t("orders.whereActivity", { consignment: change.consignment, activity: change.activity })} · `
          : change.consignment !== undefined
            ? `${t("orders.whereConsignment", { consignment: change.consignment })} · `
            : "";
      const key = FIELD_LABEL_KEYS[change.field];
      const name = key ? t(key as never) : change.field;
      const show = (raw: unknown) => {
        if (raw === null || raw === undefined || raw === "") return "—";
        if (typeof raw === "boolean") return t(raw ? "common.yes" : "common.no");
        if (change.field === "priority") return t(PRIORITY_LABEL_KEYS[raw as OrderPriority] as never);
        if (change.field === "status") return t(ORDER_STATUS[raw as keyof typeof ORDER_STATUS]?.labelKey as never);
        if (change.field === "carriers") return Array.isArray(raw) && raw.length > 0 ? carriersText(raw as CarrierCount[], t) : "—";
        if (Array.isArray(raw)) {
          // The requirements of a stop, by their names: none is "—".
          if (raw.length === 0) return "—";
          return raw
            .map((code) => {
              const key = requirementLabelKey(String(code));
              return key ? t(key as never) : String(code);
            })
            .join(", ");
        }
        // European: 10,4 and 21.000; a window as "di 3 nov 01:00–23:59", not as the API writes it.
        if (typeof raw === "number") return formatDecimal(raw);
        const window = typeof raw === "string" ? /^(\d{4}-\d{2}-\d{2}) (.+)$/.exec(raw) : null;
        if (window) return `${formatActivityDate(window[1]!)} ${window[2]}`;
        return String(raw);
      };
      return `${where}${name}: ${show(change.from)} → ${show(change.to)}`;
    });
}

const CHANNEL_SOURCE_KEYS: Record<string, string> = {
  channel: "orders.logFromChannel",
  channel_carried_out: "orders.logChannelCarriedOut",
  channel_let_go: "orders.logChannelLetGo",
  // A cancellation on a trip waits for the planner (04-10-2026).
  cancel_waiting: "orders.logCancelWaiting",
  // It replaces none of the customer's provisional orders (§3g).
  provisional_dismissed: "orders.logProvisionalDismissed",
  // A list of the customer read again, with a person's yes (Bijwerken, 25-10-2026).
  list: "orders.logFromList",
};

/** One line per change, the way a planner reads it. */
function useLogLines(): (event: OrderLogEvent) => string[] {
  const { t } = useTranslation();

  const value = (raw: unknown): string => {
    if (raw === null || raw === undefined || raw === "") return "—";
    if (typeof raw === "boolean") return t(raw ? "common.yes" : "common.no");
    return String(raw);
  };

  const fieldName = (field: string): string => {
    const key = FIELD_LABEL_KEYS[field];
    return key ? t(key as never) : field;
  };

  const changes = (fields: Record<string, unknown>, where: string): string[] =>
    Object.entries(fields)
      .filter(([, change]) => isChange(change))
      .map(([field, change]) => {
        const { from, to } = change as Change;
        return `${where}${fieldName(field)}: ${value(from)} → ${value(to)}`;
      });

  const changeLines = useChangeLines();

  return (event) => {
    const payload = event.payload ?? {};
    const lines: string[] = [];

    // A channel keeps the order up to date: say so, and whether it waited for a planner.
    const source = typeof payload["source"] === "string" ? CHANNEL_SOURCE_KEYS[payload["source"]] : undefined;
    if (source) {
      lines.push(payload["outcome"] === "held" ? t("orders.logChannelHeld") : t(source as never));
    }
    // The pallets carry changes of their own, said below.
    if (Array.isArray(payload["changes"]) && !event.eventType.startsWith("order.pallets_")) {
      lines.push(...changeLines(asArray(payload["changes"]) as unknown as ChannelChange[]));
    }

    if (event.eventType === "order.created") {
      const blockers = Array.isArray(payload["blockers"]) ? (payload["blockers"] as string[]) : [];
      const short = blockers.map((code) => (BLOCKER_SHORT_KEYS[code] ? t(BLOCKER_SHORT_KEYS[code] as never) : code));
      // Until 24-09-2026 an order entered by hand logged only the reasons of the order itself,
      // not those of its consignments: blocked stays blocked, even without its reasons.
      lines.push(
        payload["status"] === "blocked"
          ? short.length > 0
            ? t("orders.logCreatedBlocked", { reasons: short.join(", ") })
            : t("orders.logCreatedBlockedBare")
          : t("orders.logCreatedAccepted"),
      );
    }

    if (event.eventType === "order.accepted") {
      const source = payload["source"];
      lines.push(source === "blockers_solved" ? t("orders.logAcceptedSolved") : t("orders.logAcceptedAsAsked"));
      const reason = payload["reason"];
      if (typeof reason === "string") lines.push(t("orders.logReason", { reason }));
    }

    if (event.eventType === "order.rejected") {
      const reason = payload["reason"];
      if (typeof reason === "string") lines.push(t("orders.logReason", { reason }));
      const notice = payload["customerNotice"];
      const noticeKey = typeof notice === "string" ? NOTICE_LABEL_KEYS[notice as keyof typeof NOTICE_LABEL_KEYS] : undefined;
      if (noticeKey) lines.push(t("orders.logNotice", { notice: t(noticeKey as never) }));
    }

    if (event.eventType === "order.held") {
      const reason = payload["reason"];
      if (typeof reason === "string") lines.push(t("orders.logReason", { reason }));
    }

    if (event.eventType === "order.cancelled") {
      const reason = payload["reason"];
      if (typeof reason === "string") lines.push(t("orders.logReason", { reason }));
      // A provisional order, replaced by the customer's own: that one has its place on the trip now (§3g).
      if (typeof payload["replacedBy"] === "string") lines.push(t("orders.logReplacedByLine", { order: payload["replacedBy"] }));
      if (payload["byItself"] === true) lines.push(t("orders.logReplacedByItself"));
    }

    if (event.eventType === "order.repeated") {
      const made = Array.isArray(payload["made"]) ? payload["made"].filter((number): number is string => typeof number === "string") : [];
      lines.push(t(payload["provisional"] ? "orders.logRepeatedProvisional" : "orders.logRepeatedLine", { count: made.length, orders: made.join(", ") }));
    }

    if (event.eventType === "order.replaced_provisional") {
      const trips = Array.isArray(payload["trips"]) ? payload["trips"].filter((trip): trip is string => typeof trip === "string") : [];
      lines.push(t("orders.logReplacedProvisionalLine", { order: value(payload["provisional"]) }));
      if (trips.length > 0) lines.push(t("orders.logTookPlaceOnTrips", { count: trips.length, trips: trips.join(", ") }));
      if (payload["byItself"] === true) lines.push(t("orders.logReplacedByItself"));
    }

    // A remark: what it says, and whom it asked.
    if (event.eventType === "order.comment_added" || event.eventType === "order.comment_withdrawn") {
      if (typeof payload["text"] === "string" && payload["text"]) lines.push(payload["text"]);
      if (payload["image"] === true) lines.push(t("orderNotes.logWithImage"));
      const asked = Array.isArray(payload["mentions"]) ? payload["mentions"].filter((name): name is string => typeof name === "string") : [];
      if (asked.length > 0) lines.push(t("orderNotes.askedOf", { names: asked.join(", ") }));
    }

    // A task of the order: which one, and with whom it is now.
    if (event.eventType.startsWith("order.task_")) {
      lines.push([payload["taskNumber"], payload["title"]].filter((part) => typeof part === "string" && part).join(" · "));
      if (typeof payload["assigneeName"] === "string") lines.push(t("orderNotes.logTaskFor", { name: payload["assigneeName"] }));
      else if (typeof payload["mailboxName"] === "string") lines.push(t("orderNotes.logTaskMailbox", { name: payload["mailboxName"] }));
      if (typeof payload["toName"] === "string") lines.push(t("orderNotes.logTaskFor", { name: payload["toName"] }));
      if (typeof payload["note"] === "string") lines.push(t("orders.logReason", { reason: payload["note"] }));
      if (event.eventType === "order.task_done" && payload["status"] === "review") lines.push(t("orderNotes.logTaskReview"));
    }

    if (event.eventType === "order.packing_slip_added") {
      lines.push(t("orders.logPackingSlipLine", { consignment: value(payload["consignment"]), name: value(payload["fileName"]) }));
      lines.push(t("orders.logPackingSlipReprint"));
    }

    // Which mail went to the customer, and to whom.
    if (event.eventType === "order.customer_mail") {
      const kindKey = typeof payload["kind"] === "string" ? CUSTOMER_MAIL_KEYS[payload["kind"]] : undefined;
      if (kindKey) lines.push(t(kindKey as never));
      const to = Array.isArray(payload["to"]) ? payload["to"].filter((address): address is string => typeof address === "string") : [];
      if (to.length > 0) lines.push(t("orders.logCustomerMailTo", { to: to.join(", ") }));
    }

    // The slot booked at the place, and the trip mailed to it: when, and to whom.
    if (event.eventType === "order.pre_notified") {
      const slot = payload["slot"] as { date?: unknown; from?: unknown; to?: unknown } | undefined;
      if (slot && typeof slot.date === "string" && typeof slot.from === "string" && typeof slot.to === "string") {
        lines.push(t("preNotify.logSlot", { date: formatActivityDate(slot.date), from: slot.from, to: slot.to }));
      }
    }
    if (event.eventType === "order.trip_announced") {
      const to = Array.isArray(payload["to"]) ? payload["to"].filter((address): address is string => typeof address === "string") : [];
      if (payload["changed"] === true) lines.push(t("preNotify.logTripChanged"));
      if (to.length > 0) lines.push(t("orders.logCustomerMailTo", { to: to.join(", ") }));
    }

    // Direct or through a depot: which consignment, from and to, what it is now, and which trips it touched.
    if (event.eventType === "order.route_changed") {
      const name = (raw: unknown) => (typeof raw === "string" ? t((ROUTE_LABEL_KEYS[raw] ?? raw) as never) : "—");
      const where = typeof payload["consignment"] === "string" ? `${t("orders.whereConsignment", { consignment: payload["consignment"] })} · ` : "";
      lines.push(`${where}${t("orders.logRouteLine", { from: name(payload["from"]), to: name(payload["to"]) })}`);
      const legs = Array.isArray(payload["legs"]) ? payload["legs"].filter((kind): kind is string => typeof kind === "string") : [];
      if (legs.length > 0) lines.push(t("orders.logRouteLegs", { legs: legs.map((kind) => t((LEG_KIND_LABEL_KEYS[kind as LegKind] ?? kind) as never)).join(" + ") }));
      const touched = Array.isArray(payload["trips"]) ? payload["trips"].filter((trip): trip is string => typeof trip === "string") : [];
      if (touched.length > 0) lines.push(t("orders.logRouteTrips", { count: touched.length, trips: touched.join(", ") }));
      // By the rule, after a change to it or its trip-mark group: the group's metres together decided (P87).
      const mark = payload["tripMark"] as { mark?: unknown; loadingMeters?: unknown; consignments?: unknown } | undefined;
      if (mark && typeof mark.mark === "string") {
        lines.push(
          t("orders.logRouteMark", {
            mark: mark.mark,
            count: typeof mark.consignments === "number" ? mark.consignments : 1,
            metres: typeof mark.loadingMeters === "number" ? formatNumber(mark.loadingMeters) : "?",
          }),
        );
      } else if (payload["byRule"] === true) {
        lines.push(t("orders.logRouteByRule"));
      }
    }

    // The planner's depot for one consignment (Planning v3, P6): where it loads or unloads, or the trip's depot again.
    if (event.eventType === "order.depot_pinned") {
      const where = typeof payload["consignment"] === "string" ? `${t("orders.whereConsignment", { consignment: payload["consignment"] })} · ` : "";
      const depot = typeof payload["depot"] === "string" ? payload["depot"] : null;
      lines.push(
        `${where}${depot ? t(payload["leg"] === "pickup" ? "orders.logDepotUnloadsAt" : "orders.logDepotLoadsAt", { depot }) : t("orders.logDepotFollowsTrip")}`,
      );
      if (typeof payload["tripNumber"] === "string") lines.push(payload["tripNumber"]);
    }

    // A price by hand (§24.8): which consignment, the price and what it was, where it comes from, and whether it waits.
    if (event.eventType.startsWith("order.price_")) {
      const where = typeof payload["consignment"] === "string" ? `${t("orders.whereConsignment", { consignment: payload["consignment"] })} · ` : "";
      const priceText = (p: Record<string, unknown>) =>
        [
          typeof p["description"] === "string" ? p["description"] : null,
          typeof p["amount"] === "string" || typeof p["amount"] === "number" ? formatEuro(Number(p["amount"])) : null,
          typeof p["withSurcharges"] === "boolean" ? t(p["withSurcharges"] ? "orders.priceWithSurcharges" : "orders.priceAllIn").toLowerCase() : null,
        ]
          .filter(Boolean)
          .join(" · ");
      const before = asRecord(payload["from"]);
      lines.push(`${where}${before ? `${priceText(before)} → ` : ""}${priceText(payload)}`);
      const sourceKey = PRICE_SOURCE_KEYS[payload["source"] as PriceSource];
      if (sourceKey) lines.push(t(sourceKey as never));
      if (typeof payload["note"] === "string" && payload["note"]) lines.push(`${t("orders.priceNote")}: ${payload["note"]}`);
      if (typeof payload["awaits"] === "boolean") lines.push(t(payload["awaits"] ? "orders.logPriceAwaits" : "orders.logPriceStillApproved"));
    }

    // Waiting hours (§27): which consignment, the hours and what they were, and why.
    if (event.eventType.startsWith("order.waiting_")) {
      const where = typeof payload["consignment"] === "string" ? `${t("orders.whereConsignment", { consignment: payload["consignment"] })} · ` : "";
      const hoursText = (p: Record<string, unknown>) =>
        typeof p["hours"] === "string" || typeof p["hours"] === "number" ? t("orders.logWaitingHours", { hours: formatDecimal(Number(p["hours"])) }) : "";
      const before = asRecord(payload["from"]);
      lines.push(`${where}${before ? `${hoursText(before)} → ` : ""}${hoursText(payload)}`);
      if (typeof payload["note"] === "string" && payload["note"]) lines.push(`${t("orders.waitingNote")}: ${payload["note"]}`);
    }

    // No CMR back: which consignment.
    if (event.eventType.startsWith("order.no_cmr_return_") && typeof payload["consignment"] === "string") {
      lines.push(t("orders.whereConsignment", { consignment: payload["consignment"] }));
    }

    // Not to be invoiced (§27): which consignment and why.
    if (event.eventType.startsWith("order.no_invoice_")) {
      const where = typeof payload["consignment"] === "string" ? `${t("orders.whereConsignment", { consignment: payload["consignment"] })} · ` : "";
      lines.push(`${where}${typeof payload["reason"] === "string" ? payload["reason"] : ""}`);
    }

    // The pallets: where, and per kind what changed (planned or actual, unloaded or loaded).
    if (event.eventType.startsWith("order.pallets_")) {
      // A check is of the consignment, its loading and unloading together; a change is of one of them.
      const at = payload["activity"] === "load" ? t("pallets.atLoading") : payload["activity"] === "unload" ? t("pallets.atUnloading") : null;
      const place = typeof payload["place"] === "string" ? payload["place"] : null;
      const where = typeof payload["consignment"] === "string" ? t("orders.whereConsignment", { consignment: payload["consignment"] }) : null;
      lines.push([where, at, place].filter(Boolean).join(" · "));
      const changes = Array.isArray(payload["changes"]) ? payload["changes"] : [];
      for (const raw of changes) {
        const change = asRecord(raw);
        if (!change || typeof change["kind"] !== "string" || typeof change["field"] !== "string") continue;
        const field = change["field"] as string;
        const side = field.endsWith("Unloaded") ? t("pallets.unloaded") : payload["activity"] === "load" ? t("pallets.loaded") : t("pallets.returned");
        const which = `${t(field.startsWith("planned") ? "pallets.planned" : "pallets.actual")} ${side.toLowerCase()}`;
        const value = (v: unknown) => (typeof v === "number" ? String(v) : "—");
        lines.push(`${t(PALLET_KIND_LABEL_KEYS[change["kind"] as string] as never)}: ${which} ${value(change["from"])} → ${value(change["to"])}`);
      }
    }

    // The driver's melding: where, on which trip, what it was; its handling with the note; the emballage he took back.
    if (event.eventType.startsWith("order.driver_report") || event.eventType === "order.emballage_returned") {
      const at = typeof payload["activity"] === "string" ? t((DRIVER_HANDLING_KEYS[payload["activity"]] ?? "orders.activityUnload") as never) : null;
      const where = typeof payload["consignment"] === "string" ? t("orders.whereConsignment", { consignment: payload["consignment"] }) : null;
      const place = typeof payload["place"] === "string" ? payload["place"] : null;
      const trip = typeof payload["tripNumber"] === "string" ? payload["tripNumber"] : null;
      lines.push([where, at, place, trip].filter(Boolean).join(" · "));
      const text = (key: string) => (typeof payload[key] === "string" && payload[key] !== "" ? (payload[key] as string) : null);
      if (event.eventType === "order.driver_reported") {
        const facts = {
          activityType: typeof payload["activity"] === "string" ? payload["activity"] : "unload",
          answer: text("answer"),
          amount: typeof payload["amount"] === "number" ? payload["amount"] : null,
          reason: text("reason"),
          remark: text("remark"),
          colli: asRecord(payload["colli"]) as Record<string, number> | undefined,
        };
        lines.push(payload["issue"] === false ? t("orders.logDriverNoLongerIssue") : driverReportHeadline(facts, t));
        lines.push(...driverReportLines(facts, t));
        if (payload["reopened"] === true) lines.push(t("orders.logDriverReopenedByReport"));
      } else if (event.eventType === "order.emballage_returned") {
        const scans = typeof payload["scans"] === "number" ? payload["scans"] : 0;
        if (scans > 0) lines.push(t("orders.logEmballageScans", { count: scans, chep: Number(payload["chep"] ?? 0), other: Number(payload["other"] ?? 0) }));
        if (payload["allReturned"] === false) {
          const amount = typeof payload["notReturned"] === "number" ? payload["notReturned"] : null;
          lines.push(t("orders.logEmballageNotAllReturned", { amount: amount ?? "—", reason: text("notReturnedReason") ?? "—" }));
        } else if (payload["allReturned"] === true) {
          lines.push(t("orders.logEmballageAllReturned"));
        }
      } else if (text("note")) {
        lines.push(t("orders.logDriverReportNote", { note: text("note") }));
      }
    }

    // Cancelling or rejecting takes the order off the trips it was on (21-09-2026).
    if ((event.eventType === "order.cancelled" && typeof payload["replacedBy"] !== "string") || event.eventType === "order.rejected") {
      const trips = Array.isArray(payload["trips"]) ? payload["trips"].filter((trip): trip is string => typeof trip === "string") : [];
      if (trips.length > 0) lines.push(t("orders.logOffTrips", { count: trips.length, trips: trips.join(", ") }));
    }

    const priority = payload["priority"];
    if (isChange(priority)) {
      const name = (raw: unknown) => t(PRIORITY_LABEL_KEYS[raw as OrderPriority] as never);
      lines.push(`${t("orders.priorityLabel")}: ${name(priority.from)} → ${name(priority.to)}`);
    }
    const pendel = payload["pendel"];
    if (isChange(pendel)) {
      const said = (raw: unknown) => t(raw ? "common.yes" : "common.no");
      lines.push(`${t("orders.pendelLabel")}: ${said(pendel.from)} → ${said(pendel.to)}`);
    } else if (event.eventType === "order.created" && pendel === true) {
      lines.push(t("orders.pendelLabel"));
    }
    const tripMark = payload["tripMark"];
    if (isChange(tripMark)) {
      lines.push(`${t("orders.tripMarkLabel")}: ${typeof tripMark.from === "string" ? tripMark.from : "—"} → ${typeof tripMark.to === "string" ? tripMark.to : "—"}`);
    } else if (event.eventType === "order.created" && typeof tripMark === "string") {
      lines.push(`${t("orders.tripMarkLabel")}: ${tripMark}`);
    }
    // The factuurreferentie put right after intake (22-10-2026).
    const reference = payload["customerReference"];
    if (event.eventType === "order.updated" && isChange(reference)) {
      lines.push(`${t("orders.referenceLabel")}: ${typeof reference.from === "string" ? reference.from : "—"} → ${typeof reference.to === "string" ? reference.to : "—"}`);
    }

    for (const consignment of asArray(payload["consignments"])) {
      const fields = asRecord(consignment["fields"]);
      if (fields) lines.push(...changes(fields, `${t("orders.whereConsignment", { consignment: consignment["consignment"] })} · `));
    }

    for (const activity of asArray(payload["activities"])) {
      const where = `${t("orders.whereActivity", { consignment: activity["consignment"], activity: activity["activity"] })} · `;
      const fields = asRecord(activity["fields"]);
      if (fields) {
        lines.push(...changes(fields, where));
      } else if (typeof activity["from"] === "string" && typeof activity["to"] === "string") {
        // An event from before the fields were named: a window move and nothing else.
        lines.push(`${where}${t("orders.colWindow")}: ${String(activity["from"])} → ${String(activity["to"])}`);
      }
    }

    // A channel's barcodes come per consignment, as a list.
    for (const entry of asArray(payload["handlingUnits"])) {
      const count = (key: string) => (Array.isArray(entry[key]) ? (entry[key] as unknown[]).length : 0);
      const parts = [
        count("added") > 0 ? t("orders.logUnitsAdded", { count: count("added") }) : null,
        count("removed") > 0 ? t("orders.logUnitsRemoved", { count: count("removed") }) : null,
        count("changed") > 0 ? t("orders.logUnitsChanged", { count: count("changed") }) : null,
      ].filter(Boolean);
      if (parts.length > 0) {
        const where = t("orders.whereConsignment", { consignment: entry["consignment"] });
        lines.push(`${where} · ${t("orders.logUnits", { summary: parts.join(", ") })} (${t("orders.unitFromChannel")})`);
      }
    }

    const units = asRecord(payload["handlingUnits"]);
    if (units) {
      const count = (key: string) => (Array.isArray(units[key]) ? (units[key] as unknown[]).length : 0);
      const parts = [
        count("added") > 0 ? t("orders.logUnitsAdded", { count: count("added") }) : null,
        count("removed") > 0 ? t("orders.logUnitsRemoved", { count: count("removed") }) : null,
        count("changed") > 0 ? t("orders.logUnitsChanged", { count: count("changed") }) : null,
      ].filter(Boolean);
      if (parts.length > 0) {
        const where = t("orders.whereConsignment", { consignment: units["consignment"] });
        const from = units["source"] === "wms" ? ` (${t("orders.unitFromWms")})` : "";
        lines.push(`${where} · ${t("orders.logUnits", { summary: parts.join(", ") })}${from}`);
      }
    }

    const location = asRecord(payload["location"]);
    if (location) {
      const activity = asRecord(payload["activity"]);
      const where = activity
        ? `${t("orders.whereActivity", { consignment: activity["consignment"], activity: activity["activity"] })} · `
        : "";
      lines.push(`${where}${t("orders.logLocationLinked", { location: String(location["name"] ?? "") })}`);
      // Taken along when a planner linked a place on another order, or when the address book
      // came to know the place (24-09-2026, S62); and the spelling a link taught WLX.
      if (typeof payload["linkedAlong"] === "string") lines.push(t("orders.logLinkedAlong", { order: payload["linkedAlong"] }));
      if (payload["linkedByAddressBook"] === true) lines.push(t("orders.logLinkedByAddressBook"));
      if (typeof payload["rememberedSpelling"] === "string") {
        lines.push(t("orders.logRememberedSpelling", { spelling: payload["rememberedSpelling"] }));
      }
    }

    // A planned activity that moved took its trip along, or came off it on another day (21-09-2026); since 04-10-2026
    // another day waits on the trip for the planner.
    const planning = asRecord(payload["planning"]);
    if (planning) {
      const numbers = (key: string) =>
        Array.isArray(planning[key]) ? (planning[key] as unknown[]).filter((trip): trip is string => typeof trip === "string") : [];
      const followed = numbers("followed");
      const off = numbers("off");
      const waiting = numbers("waiting");
      if (followed.length > 0) lines.push(t("orders.logTripFollowed", { count: followed.length, trips: followed.join(", ") }));
      if (off.length > 0) lines.push(t("orders.logTripOff", { count: off.length, trips: off.join(", ") }));
      if (waiting.length > 0) lines.push(t("orders.logTripWaiting", { count: waiting.length, trips: waiting.join(", ") }));
    }
    // Where a cancellation waits for the planner.
    if (payload["source"] === "cancel_waiting" && Array.isArray(payload["trips"])) {
      const trips = (payload["trips"] as unknown[]).filter((trip): trip is string => typeof trip === "string");
      if (trips.length > 0) lines.push(t("orders.logCancelWaitingOn", { count: trips.length, trips: trips.join(", ") }));
      if (typeof payload["reason"] === "string") lines.push(t("orders.logReason", { reason: payload["reason"] }));
    }

    // What the channel's message said besides the order: the file it came in, and the customer's
    // references for the report back — on FrieslandCampina's IFCSUM the shuttle's ritnummer is
    // omschrijving2 (Camiel, 28-09-2026: "ik zie het ritnummer niet?"). Under the sender's own names,
    // since what they mean is not fixed (transportopdracht-naar-order.xslt).
    const context = asRecord(payload["context"]);
    if (context) {
      const parts = Object.entries(context)
        .filter(([, raw]) => typeof raw === "string" && raw !== "")
        .map(([name, raw]) => `${name} ${raw as string}`);
      if (parts.length > 0) lines.push(t("orders.logContext", { values: parts.join(" · ") }));
    }

    return lines;
  };
}

/** The log of an order, kept in one place so the card can also show its last line. */
export function useOrderLog(orderId: string, enabled = true) {
  return useQuery({ queryKey: ordersKeys.log(orderId), queryFn: () => getOrderLog(orderId), enabled });
}

/** What an event is called, for a line outside the list itself. */
export function useEventName(): (event: OrderLogEvent) => string {
  const { t } = useTranslation();
  return (event) => {
    const key = EVENT_LABEL_KEYS[event.eventType];
    return key ? (t(key as never) as string) : event.eventType;
  };
}

/**
 * The log of one order. On the card it is a tab of its own and stands open; elsewhere it
 * still folds away behind Logboek tonen.
 */
export function OrderLog({ orderId, alwaysOpen = false }: { orderId: string; alwaysOpen?: boolean }) {
  const { t } = useTranslation();
  const lines = useLogLines();
  const eventName = useEventName();
  const [open, setOpen] = useState(alwaysOpen);
  const query = useOrderLog(orderId, open);
  const events = query.data ?? [];

  return (
    <div className="wl-card" style={{ padding: "var(--space-4) var(--space-6)" }}>
      {!alwaysOpen && (
        <button
          type="button"
          className="wl-btn wl-btn--ghost wl-btn--sm"
          aria-expanded={open}
          style={{ paddingLeft: 0 }}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? t("orders.logHide") : t("orders.logShow")}
        </button>
      )}

      {open && (
        <div style={{ marginTop: alwaysOpen ? 0 : "var(--space-3)" }}>
          {query.isLoading && <p className="t-caption t-muted">{t("common.loading")}</p>}
          {!query.isLoading && events.length === 0 && <p className="t-caption t-muted">{t("orders.logEmpty")}</p>}
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {events.map((event, index) => (
              <li
                key={event.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "130px minmax(0, 1fr)",
                  gap: "var(--space-3)",
                  padding: "var(--space-2_5) 0",
                  borderTop: index === 0 ? undefined : "1px solid var(--border-subtle)",
                }}
              >
                <span className="t-caption t-mono t-muted">{formatReceived(event.occurredAt)}</span>
                <div className="wl-vstack" style={{ gap: "2px", alignItems: "stretch", minWidth: 0 }}>
                  <div className="wl-hstack" style={{ gap: "var(--space-2)", flexWrap: "wrap", alignItems: "baseline" }}>
                    <span className="t-body-sm" style={{ fontWeight: "var(--fw-medium)" }}>
                      {eventName(event)}
                    </span>
                    <span className="t-caption t-muted">
                      {event.actorName ?? (event.actorType === "system" ? t("orders.logBySystem") : t("orders.logByNobody"))}
                    </span>
                  </div>
                  {lines(event).map((line, position) => (
                    // A file name from the channel is one long word: let it break rather than widen the card.
                    <span key={position} className="t-caption t-muted" style={{ overflowWrap: "anywhere", whiteSpace: "pre-line" }}>
                      {line}
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
